import React, { useState, useEffect } from "react";
import { useSidebar } from "../../context/SidebarContext"; // Adjust path if necessary
import { affiliateAPI } from "../../services/AffiliatePartnerPortal/affiliateAPI";
import { useAuth } from "../../context/AuthContext";
import LeadManagementContent from "../../components/AffiliatePartnerPortal/LeadManagementContent";
import CustomerManagementContent from "../../components/AffiliatePartnerPortal/CustomerManagementContent";
import NetworkManagementContent from "../../components/AffiliatePartnerPortal/NetworkManagementContent";
const AffiliateLeads = () => {
  const { sidebarExpanded, isMobile } = useSidebar();
  const { user } = useAuth();
  const id = user.id;
  console.log("Current logged-in user ID:", id);

  // State for search and filtering
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  // State for data and loading
  const [leads, setLeads] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for checkbox selection
  const [selectAll, setSelectAll] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [activeTab, setActiveTab] = useState("application-list");

  // Status options
  const statusOptions = [
    { value: "All", label: "All Statuses" },
    { value: "new-leads", label: "New Leads" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
    { value: "processing", label: "Processing" },
    { value: "submitted", label: "Submitted" },
    { value: "in-review", label: "In Review" },
  ];

  // Fetch leads data
  const fetchLeads = async () => {
    try {
      setLoading(true);
      setError(null);

      const user = JSON.parse(localStorage.getItem("user"));
      if (!user || !user.id) {
        throw new Error("User not authenticated");
      }

      const response = await affiliateAPI.getAffiliateLeads(id);
      console.log("Raw API Response:", response);
      console.log("Response data:", response.data);

      if (response.success && response.data) {
        console.log("Setting leads data:", response.data);
        setLeads(response.data);
      } else {
        console.warn("Invalid leads data structure:", response);
        throw new Error(response.message || "Invalid leads data structure");
      }
    } catch (err) {
      console.error("Error in fetchLeads:", err);
      setError(err.message || "Failed to fetch leads. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      setError(null);
      const user = JSON.parse(localStorage.getItem("user"));
      if (!user || !user.id) {
        throw new Error("User not authenticated");
      }
      const response = await affiliateAPI.getAffiliateCustomers(id);
      if (response.success && response.data) {
        setCustomers(response.data);
      } else {
        throw new Error(response.message || "Invalid customer data structure");
      }
    } catch (err) {
      console.error("Error in fetchCustomers:", err);
      setError(
        err.message || "Failed to fetch customers. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  };

  // Fetch leads on component mount
  useEffect(() => {
    fetchLeads();
    fetchCustomers();
  }, []);

  // Filter leads based on search term, selected status, and date range
  const filteredLeads = leads.filter((lead) => {
    console.log("Filtering lead:", lead);

    // Search by loan amount or loan type
    const matchesSearch =
      searchTerm === "" ||
      (lead.loanAmount && lead.loanAmount.toString().includes(searchTerm)) ||
      (lead.loanType &&
        lead.loanType.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus =
      selectedStatus === "All" ||
      lead.status?.toLowerCase().replace(/[_\s]/g, "-") === selectedStatus;

    let matchesDate = true;
    if (startDate && endDate && lead.applicationDate) {
      const [day, month, year] = lead.applicationDate.split("/");
      const leadDate = new Date(year, month - 1, day);
      matchesDate = leadDate >= startDate && leadDate <= endDate;
    }

    console.log("Filter results:", {
      matchesSearch,
      matchesStatus,
      matchesDate,
    });
    return matchesSearch && matchesStatus && matchesDate;
  });

  const filteredCustomers = customers.filter((customer) => {
    const matchesSearch =
      searchTerm === "" ||
      (customer.name &&
        customer.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (customer.phone && customer.phone.includes(searchTerm));
    let matchesDate = true;
    if (startDate && endDate && customer.dateAdded) {
      const customerDate = new Date(customer.dateAdded);
      matchesDate = customerDate >= startDate && customerDate <= endDate;
    }
    return matchesSearch && matchesDate;
  });

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle status selection
  const handleStatusChange = (status) => {
    setSelectedStatus(status);
    setIsStatusDropdownOpen(false);
  };

  // Handle select all checkbox
  const handleSelectAllLeads = () => {
    setSelectAll(!selectAll);
    setSelectedLeads(selectAll ? [] : filteredLeads.map((lead) => lead.id));
  };

  // Handle individual lead selection
  const handleSelectLead = (leadId) => {
    const newSelected = selectedLeads.includes(leadId)
      ? selectedLeads.filter((id) => id !== leadId)
      : [...selectedLeads, leadId];
    setSelectedLeads(newSelected);
  };

  // Handle select all customers
  const handleSelectAllCustomers = () => {
    setSelectAll(!selectAll);
    setSelectedCustomers(selectAll ? [] : filteredCustomers.map((c) => c.id));
  };

  // Handle customer selection
  const handleSelectCustomer = (customerId) => {
    const newSelected = selectedCustomers.includes(customerId)
      ? selectedCustomers.filter((id) => id !== customerId)
      : [...selectedCustomers, customerId];
    setSelectedCustomers(newSelected);
  };

  // Update selectAll state when individual selections change
  useEffect(() => {
    if (activeTab === "application-list") {
      setSelectAll(
        filteredLeads.length > 0 &&
          selectedLeads.length === filteredLeads.length
      );
    } else {
      setSelectAll(
        filteredCustomers.length > 0 &&
          selectedCustomers.length === filteredCustomers.length
      );
    }
  }, [
    selectedLeads,
    selectedCustomers,
    filteredLeads,
    filteredCustomers,
    activeTab,
  ]);

  // Helper function to get status style
  const getStatusStyle = (status) => {
    switch (status.toLowerCase()) {
      case "approved":
        return "bg-[#4ADE8033] text-[#4ADE80]";
      case "rejected":
        return "bg-[#FF0000] text-red-300";
      case "in-review":
        return "bg-[#CCCCFF33] text-[#CCCCFF]";
      case "processing":
        return "bg-[#423715] text-[#FFCA20]";
      case "submitted":
        return "bg-purple-600 text-purple-300";
      case "new-leads":
        return "bg-gray-600 text-gray-300";
      default:
        return "bg-gray-600 text-gray-300";
    }
  };

  return (
    <div
      className="min-h-screen bg-black text-white   mt-12 p-4 md:p-6"
      style={{
        marginLeft: isMobile ? "0" : sidebarExpanded ? "200px" : "60px",
        width: isMobile
          ? "100%"
          : `calc(100% - ${sidebarExpanded ? "200px" : "60px"})`,
      }}
    >
      <div className="font-medium text-[24px] leading-[100%] mb-[2px] tracking-[0] font-inter">
        Lead Management
      </div>
      <div className="bg-[#000000] p-4 pt-0 pl-8 flex flex-col sm:flex-row justify-between items-start gap-4 sm:gap-0 mb-6 border-b border-[#333333]">
        <div className="overflow-x-auto mt-3 mb-[-1rem]">
          <div className="flex min-w-max space-x-6">
            <button
              onClick={() => setActiveTab("application-list")}
              className={`tab-button ${
                activeTab === "application-list"
                  ? "active-tab-border"
                  : "inactive-tab"
              }`}
            >
              Application List
            </button>
            <button
              onClick={() => setActiveTab("customer-list")}
              className={`tab-button ${
                activeTab === "customer-list"
                  ? "active-tab-border"
                  : "inactive-tab"
              }`}
            >
              Customer List
            </button>
             <button
              onClick={() => setActiveTab("network-list")}
              className={`tab-button ${
                activeTab === "network-list"
                  ? "active-tab-border"
                  : "inactive-tab"
              }`}
            >
              Network List
            </button>
          </div>
        </div>
      </div>
      {/* <h1 className="text-2xl font-medium mb-4">Lead Management</h1> */}
      {activeTab === "application-list" && (
        <LeadManagementContent
          statusOptions={statusOptions}
          selectedStatus={selectedStatus}
          isStatusDropdownOpen={isStatusDropdownOpen}
          setIsStatusDropdownOpen={setIsStatusDropdownOpen}
          handleStatusChange={handleStatusChange}
          searchTerm={searchTerm}
          handleSearchChange={handleSearchChange}
          isDatePickerOpen={isDatePickerOpen}
          setIsDatePickerOpen={setIsDatePickerOpen}
          startDate={startDate}
          endDate={endDate}
          setDateRange={setDateRange}
          selectAll={selectAll}
          handleSelectAll={handleSelectAllLeads}
          selectedLeads={selectedLeads}
          loading={loading}
          error={error}
          filteredLeads={filteredLeads}
          handleSelectLead={handleSelectLead}
          getStatusStyle={getStatusStyle}
        />
      )}
      {activeTab === "customer-list" && (
        <CustomerManagementContent
          searchTerm={searchTerm}
          handleSearchChange={handleSearchChange}
          isDatePickerOpen={isDatePickerOpen}
          setIsDatePickerOpen={setIsDatePickerOpen}
          startDate={startDate}
          endDate={endDate}
          setDateRange={setDateRange}
          selectAll={selectAll}
          handleSelectAll={handleSelectAllCustomers}
          selectedCustomers={selectedCustomers}
          loading={loading}
          error={error}
          filteredCustomers={filteredCustomers}
          handleSelectCustomer={handleSelectCustomer}
        />
      )}
      {activeTab === "network-list" && (
        <NetworkManagementContent
          searchTerm={searchTerm}
          handleSearchChange={handleSearchChange}
          isDatePickerOpen={isDatePickerOpen}
          setIsDatePickerOpen={setIsDatePickerOpen}
          startDate={startDate}
          endDate={endDate}
          setDateRange={setDateRange}
          selectAll={selectAll}
          handleSelectAll={handleSelectAllCustomers}
          selectedCustomers={selectedCustomers}
          loading={loading}
          error={error}
          filteredCustomers={filteredCustomers}
          handleSelectCustomer={handleSelectCustomer}
        />
      )}
    </div>
  );
};

export default AffiliateLeads;
